import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import { routeConstants } from '../../../constants/routeConstants';
import {
  useGetWeeklyGLPostingByIdQuery,
  useCreateWeeklyGLPostingMutation,
} from '../../../services/WeeklyGLPosting/WeeklyGLPostingService';
import { ROUTES } from '../constants';
import { useGLPostingFilters } from './useGLPostingFilters';
import { useGLPostingModals } from './useGLPostingModals';
import { resolveCustomerName } from '../utils/glPostingMethods';
import { useOpenPostingRestoreState } from './useOpenPostingRestoreState';
import { useOpenPostingPersistenceEffects } from './useOpenPostingPersistenceEffects';
import { useGLPostingDataPanel } from './useGLPostingDataPanel';
import { buildApprovedPostingData } from './utils/approvedPostingData';

export const useGLPostingDetails = ({ isEmbedded = false } = {}) => {
  const { id } = useParams();
  const normalizedPostingId = String(id ?? '').trim();
  const location = useLocation();
  const currentUserId = useSelector((state) => state.user?.userDetails?.id);

  const {
    data: postingDetailsFromApi,
    isError: isPostingDetailsError,
    error: postingDetailsError,
  } = useGetWeeklyGLPostingByIdQuery(normalizedPostingId, {
    skip: !normalizedPostingId,
  });

  const postingDetails = useMemo(() => postingDetailsFromApi || null, [postingDetailsFromApi]);

  const isApprovedPosting = useMemo(
    () =>
      !isEmbedded &&
      (Boolean(location.state?.isApproved) ||
        location.pathname.includes(`/${routeConstants.WEEKLY_GL_POSTING_APPROVED}/`)),
    [isEmbedded, location.pathname, location.state?.isApproved],
  );

  const {
    shouldPersistOpenPostingState,
    shouldRestoreFromPersistedState,
    persistedWarnings,
    persistedFeedSourceData,
    persistedIsApplyingFilters,
  } = useOpenPostingRestoreState({ isApprovedPosting, normalizedPostingId });

  const approvedPostingData = useMemo(
    () =>
      buildApprovedPostingData({
        isApprovedPosting,
        rowState: location.state?.selectedRow,
        postingDetails,
        normalizedPostingId,
      }),
    [isApprovedPosting, location.state?.selectedRow, postingDetails, normalizedPostingId],
  );

  const headerData = useMemo(() => {
    const customerName = resolveCustomerName(postingDetails);
    return customerName ? { customerName } : null;
  }, [postingDetails]);

  const {
    warnings,
    warningCount,
    feedSourceData,
    sublocationBreakdown,
    resyncData,
    isApplyingFilters,
    isFetchingFeeds,
    showApiErrorModal,
    apiErrorMessage,
    handleApiError,
    handleCloseApiErrorModal,
    handleApplyFilters,
    resetDataPanelState,
  } = useGLPostingDataPanel({
    postingDetails,
    isApprovedPosting,
    normalizedPostingId,
    persistedWarnings,
    persistedFeedSourceData,
    persistedIsApplyingFilters,
  });

  const { clearPersistedOpenPostingState } = useOpenPostingPersistenceEffects({
    shouldPersistOpenPostingState,
    shouldRestoreFromPersistedState,
    warnings,
    feedSourceData,
    isApplyingFilters,
  });

  const [isApproved, setIsApproved] = useState(isApprovedPosting);

  useEffect(() => {
    setIsApproved(isApprovedPosting);
  }, [isApprovedPosting]);

  useEffect(() => {
    if (!normalizedPostingId || !isPostingDetailsError) return;
    handleApiError(postingDetailsError);
  }, [normalizedPostingId, isPostingDetailsError, postingDetailsError, handleApiError]);

  const handleResetState = useCallback(() => {
    setIsApproved(false);
    resetDataPanelState();
  }, [resetDataPanelState]);

  const { filters, sublocationState } = useGLPostingFilters({
    onResetState: handleResetState,
    onApplyFilters: handleApplyFilters,
    skipProductTypeFetch: isApprovedPosting,
    onApiError: handleApiError,
    persistOpenPostingState: shouldPersistOpenPostingState,
    restoreFromPersistedState: shouldRestoreFromPersistedState,
  });

  const validateForApproval = useCallback(() => [], []);

  const handleApprovalSuccess = useCallback(() => {
    setIsApproved(true);
    clearPersistedOpenPostingState();
  }, [clearPersistedOpenPostingState]);

  const buildPostingData = useCallback(() => {
    const { selectedX3ProductName, selectedStartDate, selectedEndDate, selectedCurrencies, selectedPostingDate } =
      filters;
    const { selectedSublocations } = sublocationState;

    const currencyIds = (Array.isArray(selectedCurrencies) ? selectedCurrencies : [])
      .map((idValue) => Number(idValue))
      .filter((idValue) => Number.isInteger(idValue) && idValue > 0);

    if (
      !selectedX3ProductName ||
      currencyIds.length === 0 ||
      !selectedStartDate ||
      !selectedEndDate ||
      !selectedPostingDate ||
      !selectedSublocations?.length
    ) {
      return null;
    }

    const sublocationIds = selectedSublocations
      .map((idValue) => Number(idValue))
      .filter((idValue) => Number.isInteger(idValue) && idValue > 0);

    if (!sublocationIds.length) {
      return null;
    }

    return {
      ...(currentUserId ? { createdBy: currentUserId } : {}),
      x3ProductName: selectedX3ProductName,
      sublocationIds,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      postingDate: selectedPostingDate,
    };
  }, [filters, sublocationState, currentUserId]);

  const [createPosting] = useCreateWeeklyGLPostingMutation();

  const modals = useGLPostingModals({
    validateForApproval,
    onApprovalSuccess: handleApprovalSuccess,
    createPosting,
    buildPostingData,
  });

  const isApproveDisabled = useMemo(
    () => isApproved || modals.isSubmitting || !filters.selectedPostingDate,
    [isApproved, modals.isSubmitting, filters.selectedPostingDate],
  );

  const breadcrumbData = useMemo(() => {
    const weeklyGLPostingNames = BREADCRUMB_NAMES?.WEEKLY_GL_POSTING ?? {};
    const { WEEKLY_GL_POSTING = 'Weekly GL Posting', GL_POSTING_DETAIL = 'GL Posting Detail' } = weeklyGLPostingNames;

    return [
      {
        text: WEEKLY_GL_POSTING,
        redirection: `${routeConstants.WEEKLY_GL_POSTING_ROUTE}${ROUTES.QUERY_STRING_APPROVED}`,
      },
      { text: GL_POSTING_DETAIL },
    ];
  }, []);

  return {
    // Page metadata
    sublocationId: id,
    breadcrumbData,
    headerData,

    // Filter dropdowns state
    filters,

    // Sublocation selector state
    sublocationState,

    // Posting/approval state
    postingState: {
      isApprovedPosting,
      approvedPostingData,
      isApproved,
      isSubmitting: modals.isSubmitting,
      isApproveDisabled,
      onApproveClick: modals.onApproveClick,
    },

    // Warnings and feed source data
    dataPanel: {
      warnings,
      warningCount,
      feedSourceData,
      sublocationBreakdown,
      resyncData,
      isApplyingFilters,
      isFetchingFeeds,
    },

    // Modal state
    modals: {
      showApproveModal: modals.showApproveModal,
      showSuccessModal: modals.showSuccessModal,
      showFailureModal: modals.showFailureModal,
      failureMessage: modals.failureMessage,
      showApiErrorModal,
      apiErrorMessage,
      validationErrors: modals.validationErrors,
      createdPosting: modals.createdPosting,
      onApproveConfirm: modals.onApproveConfirm,
      onFailureAction: modals.onFailureAction,
      onCloseSuccess: modals.onCloseSuccess,
      onCloseApiErrorModal: handleCloseApiErrorModal,
    },
  };
};
